import React, { useEffect, useState } from 'react';
import { VideoAsset } from 'src/common/helpers/video_asset';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import JourneyEmailGating from 'src/player/JourneyEmailGating';
import { Nullable } from 'src/types/nullable.type';
import { apiUnlockJourney, apiCreateEmailVerification, apiVerifyEmail, apiCheckIfNdaSigned, apiSignNda } from 'src/utils/journeyApi';

export const EmbedPlayerEmailGating = ({
  journey,
  userEmail,
  uuid,
  emailRequired,
  passwordRequired,
  emailVerificationRequired,
  ndaSignatureRequired,
  introVideoAsset,
  trackingUUID,
  sessionUUID,
  onNdaSigned,
  onEmailSubmitted,
  onJourneyUnlocked,
}: {
  journey: any;
  uuid: string;
  userEmail: Nullable<string>;
  emailRequired: boolean;
  emailVerificationRequired: boolean;
  ndaSignatureRequired: boolean;
  trackingUUID: string;
  sessionUUID: string;
  passwordRequired: boolean;
  introVideoAsset?: VideoAsset | null;
  onNdaSigned: (email: string, signature: string) => void;
  onEmailSubmitted: (email: string) => void;
  onJourneyUnlocked: (email?: string) => Promise<any>;
}) => {
  const [accessCodeRequired, setAccessCodeRequired] = useState(false);
  const { setSuccessNotification } = useNotificationMessage();

  const trackSegmentEvent = (eventName: string, email: string = '') => {
    window.analytics &&
      window.analytics.track(eventName, {
        email: email,
        journey_alias_uuid: journey.alias.uuid,
      });
  };

  useEffect(() => {
    if (emailRequired) {
      trackSegmentEvent('Email Gate Opened', '');
    }
    // @anilsevim
    // for some reason when i include trackSegmentEvent, it constantly triggers this so we don't want that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailRequired]);

  const unlockJourney = (email: string, password: string = '') =>
    apiUnlockJourney(uuid, {
      email,
      password,
      tracking_uuid: trackingUUID,
      session_uuid: sessionUUID,
    });

  const onUnlockSubmitted = ({
    email,
    password,
    access_code,
  }: {
    email: string;
    password: string;
    access_code: string | null;
  }) => {
    onEmailSubmitted(email);
    if (accessCodeRequired) {
      return apiVerifyEmail(journey.alias.uuid, access_code).then(() => {
        return onJourneyUnlocked(email);
      });
    } else if (emailVerificationRequired) {
      return unlockJourney(email, password).then(() => {
        return apiCreateEmailVerification(journey.alias.uuid, email).then(() => {
          setAccessCodeRequired(true);
          setSuccessNotification('An access code is sent to your email. Please enter that code below.');
          trackSegmentEvent('Email Gate Access Code Sent', email);
        });
      });
    } else if (emailRequired || passwordRequired) {
      return unlockJourney(email, password).then(() => {
        return onJourneyUnlocked(email);
      });
    }

    return onJourneyUnlocked();
  };

  const onCodeExpired = () => {
    setAccessCodeRequired(false);
  };

  const showEmailGate = emailRequired || emailVerificationRequired || passwordRequired || ndaSignatureRequired;

  if (!showEmailGate) return null;

  return (
    <JourneyEmailGating
      journey={journey}
      email={userEmail}
      emailRequired={emailRequired}
      passwordRequired={passwordRequired}
      accessCodeRequired={accessCodeRequired}
      emailVerificationRequired={emailVerificationRequired}
      ndaSignatureRequired={ndaSignatureRequired}
      introVideoAsset={introVideoAsset}
      onSubmit={onUnlockSubmitted}
      onCodeExpired={onCodeExpired}
      onBypassClick={onJourneyUnlocked}
      onNdaSigned={onNdaSigned}
    />
  );
};
